<template>
  <code-reader
    v-if="showCodeCamera"
    ref="codeReader"
    @decode="handleDecodeCodeData"
    @cancel="handleCancelCodeCamera"
    class="animate__animated animate__fadeIn"
  ></code-reader>

  <div v-else>
    <navbar :show-display-name="false">
      <template v-slot:navbarTitle>
        <h1 class="h5 m-0">{{ inspection_type.name }}</h1>
      </template>
      <template v-slot:navbarButton>
        <router-link
          :to="{
            name: 'AppHomeOperate',
          }"
          class="btn btn-dark"
          tabindex="-1"
          ><i class="bi bi-0-square me-1"></i>戻る</router-link
        >
      </template>
    </navbar>

    <main id="main" class="bg-light">
      <div class="container py-3 py-lg-4">
        <div class="row row-cols-1 gy-2 gy-lg-3">
          <div class="col">
            <form class="card">
              <div class="card-body p-2 p-lg-3">
                <search-form
                  :query="query"
                  query-key="q"
                  placeholder="指示番号で検索"
                  :use-code-reader="true"
                  @start-code-camera="handleStartCodeCamera"
                  @update-search-query="handleUpdateSearchQuery"
                >
                  <template v-slot:searchButton> 検索 </template>
                </search-form>
              </div>
            </form>
          </div>

          <div class="col">
            <div v-if="inspections && inspections.length > 0">
              <div class="row row-cols-1 row-cols-md-2 g-1 g-lg-2 mb-3">
                <div
                  v-for="(inspection, index) in inspections"
                  :key="inspection.id"
                  class="col"
                >
                  <div class="card h-100">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div class="m-0">
                          <h5
                            class="card-subtitle mb-2 stretched-link"
                            style="cursor: pointer"
                            :ref="'link' + (index + 1)"
                            @click.prevent="moveDetail(inspection)"
                          >
                            <i
                              class="bi me-1"
                              :class="'bi-' + parseFloat(index + 1) + '-square'"
                            ></i
                            >{{ inspection.product_model }}
                          </h5>
                          <h5 class="card-title m-0">
                            {{ inspection.inspection_name }}
                          </h5>
                        </div>
                        <div>
                          <span
                            v-if="inspection.has_image"
                            class="badge bg-primary"
                            >画像あり</span
                          ><span v-else class="badge bg-secondary"
                            >画像なし</span
                          >
                        </div>
                      </div>
                    </div>
                    <div class="card-footer d-flex justify-content-end">
                      <div class="small text-end">
                        {{ inspection.inspection_type_name }}&nbsp;
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-center gap-2 mb-2">
                <pagination
                  :prev="prev"
                  :next="next"
                  ref="pagination"
                  @move-page="movePage"
                ></pagination>
              </div>
            </div>
            <div v-else class="alert alert-warning" role="alert">
              <span v-if="isSearching">該当するデータはありません</span>
              <span v-else>指示番号を入力してください</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import CodeReader from '@/components/AppCodeReader.vue'
import Pagination from '@/components/AppNoCountPagination.vue'
import SearchForm from '@/components/common/SearchForm.vue'
import Navbar from '@/components/operate/OperateNavbar.vue'
import { trimToDigits } from '@/utils/stringUtils.js'
import store from '../store'

export default {
  name: 'OperateInspectionMonitor',
  components: {
    CodeReader,
    Navbar,
    Pagination,
    SearchForm,
  },

  data() {
    return {
      query: {},
      showCodeCamera: false,
      isSearching: false,
    }
  },
  computed: {
    inspection_type() {
      return store.getters['operate_inspection_plan/inspection_type']
    },
    inspections() {
      return store.getters['operate_inspection_plan/results']
    },
    prev() {
      return store.getters['operate_inspection_plan/prev']
    },
    next() {
      return store.getters['operate_inspection_plan/next']
    },
    saveQuery() {
      return store.getters['operate_inspection_plan/query']
    },
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyDown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },

  async created() {
    if (!this.inspection_type || !this.inspection_type.id) {
      this.$store.dispatch('alert/setErrorMessage', {
        message: '不正なアクセスです。',
      })
      window.setTimeout(() => {
        this.$router.push({
          name: 'AppHomeOperate',
        })
      }, this.$store.getters['alert/timeout'])
    } else {
      this.query = this.saveQuery
    }
  },
  beforeRouteLeave(to, from, next) {
    // 詳細画面以外のページに移動する場合にデータをクリアする
    if (to.name !== 'OperateInspectionMonitorDetail') {
      store.dispatch('operate_inspection_plan/clearAll')
    }
    next()
  },

  methods: {
    trimToDigits,
    getData() {
      this.isSearching = true
      return store.dispatch('operate_inspection_plan/fetchAll')
    },
    async getQueryData() {
      store.commit('operate_inspection_plan/setQuery', this.query)
      await this.getData()
      // 1件なら自動的に詳細画面に遷移する
      if (
        (!this.query.page || this.query.page === 1) &&
        this.inspections.length === 1
      ) {
        // 遷移前に選択したデータを store に保持する
        store.commit(
          'operate_inspection_plan/setSelectedInspectionPlan',
          this.inspections[0],
        )
        this.$router.push({
          name: 'OperateInspectionMonitorDetail',
          params: { id: this.inspections[0].inspection_id },
        })
      }
    },
    clearData() {
      this.isSearching = false
      store.commit('operate_inspection_plan/clearData')
    },
    async movePage(page) {
      this.query = Object.assign(this.query, { page: page })
      await this.getQueryData()
    },
    moveDetail(inspection) {
      // 遷移前に選択したデータを store に保持する
      store.commit(
        'operate_inspection_plan/setSelectedInspectionPlan',
        inspection,
      )
      this.$router.push({
        name: 'OperateInspectionMonitorDetail',
        params: { id: inspection.inspection_id },
      })
    },
    handleStartCodeCamera() {
      this.showCodeCamera = true
    },
    handleCancelCodeCamera() {
      this.showCodeCamera = false
    },
    async handleDecodeCodeData(result) {
      // QRコードのデータから指示番号を抽出してクエリにセットする
      this.query.q = this.trimToDigits(result, 6)
      console.log('handleDecodeCodeData', this.query.q)
      await this.handleCancelCodeCamera()
      await this.getQueryData()
    },
    async handleUpdateSearchQuery(query = {}) {
      if (!query.q) {
        this.clearData()
        return
      }
      if (query.q === this.query.q) {
        return
      }
      this.query = query
      await this.getQueryData()
    },
    handleKeyDown(event) {
      if (document.activeElement.tagName.toLowerCase() === 'input') {
        return
      }
      switch (event.key) {
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6': {
          const linkRef = this.$refs['link' + event.key]
          // キーに対応するデータが存在していれば詳細画面に遷移する
          if (linkRef && linkRef[0]) {
            this.moveDetail(this.inspections[parseInt(event.key) - 1])
          }
          break
        }
        case '9':
          this.handleStartCodeCamera()
          break
        case '0':
          this.$router.push({ name: 'AppHomeOperate' })
          break
        case '+':
          this.$refs.pagination.moveNext()
          break
        case '-':
          this.$refs.pagination.movePrev()
          break
        case 'Escape':
        case 'Backspace':
          this.$refs.codeReader.cancelStream()
          break
        default:
          break
      }
    },
  },
}
</script>
